import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import {
  FolderOpen,
  Video,
  Image as ImageIcon,
  Clock,
  Upload,
  CheckCircle2,
  AlertTriangle,
  PlayCircle,
  FileImage,
  Info,
  Download,
  FileText,
  Plus,
  Eye,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
  Loader2,
  Send,
} from "lucide-react";
import Card from "./ui/Card";
import Button from "./ui/Button";

const CreativeSubmissionPage = () => {
  const { user } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState("submit");

  // Submission form state
  const [driveLink, setDriveLink] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Submissions list state
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch existing submissions
  useEffect(() => {
    if (user?.id) {
      fetchSubmissions();
    }
  }, [user?.id]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("creative_submissions")
        .select(
          `
          *,
          reviewed_by_profile:reviewed_by(username)
        `
        )
        .eq("affiliate_id", user.id)
        .order("submitted_at", { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error("Error fetching submissions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!driveLink.trim()) return;

    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      if (!driveLink.includes("drive.google.com")) {
        throw new Error("Please provide a valid Google Drive link");
      }

      const { error } = await supabase.from("creative_submissions").insert([
        {
          affiliate_id: user.id,
          link_url: driveLink,
          status: "pending",
        },
      ]);

      if (error) throw error;

      setDriveLink("");
      setIsSubmitted(true);
      setSuccess(
        "Creative submitted successfully! You'll receive feedback within 24 hours."
      );
      fetchSubmissions(); // Refresh the list
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case "rejected":
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "text-green-400 bg-green-400/20 border-green-400/30";
      case "rejected":
        return "text-red-400 bg-red-400/20 border-red-400/30";
      default:
        return "text-yellow-400 bg-yellow-400/20 border-yellow-400/30";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#B8460E]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#B8460E]/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#B8460E]/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-6 py-6 space-y-6">
        {/* Header Section */}
        <div className="text-center space-y-3 sm:space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-[#B8460E] to-[#8B3A0E] rounded-xl shadow-lg shadow-[#B8460E]/25">
            <Upload className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-3 tracking-tight">
              Creative Submission Portal
            </h1>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Submit your creative assets for professional review and approval
              by our media buying team
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-black/30 backdrop-blur-2xl border border-white/10 rounded-xl p-2 shadow-xl">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab("submit")}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                activeTab === "submit"
                  ? "bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] text-white shadow-lg shadow-[#B8460E]/25"
                  : "text-gray-300 hover:text-white hover:bg-white/10"
              }`}
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="leading-tight">Submit New Creative</span>
            </button>
            <button
              onClick={() => setActiveTab("submissions")}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-300 ${
                activeTab === "submissions"
                  ? "bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] text-white shadow-lg shadow-[#B8460E]/25"
                  : "text-gray-300 hover:text-white hover:bg-white/10"
              }`}
            >
              <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="leading-tight">My Submissions</span>
              {submissions.length > 0 && (
                <span className="bg-white/20 text-white text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full font-medium">
                  {submissions.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === "submit" ? (
          <div className="space-y-6">
            {/* Submission Form */}
            <Card className="p-4 sm:p-6 lg:p-8">
              <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-[#B8460E]/20 to-[#8B3A0E]/20 rounded-lg flex items-center justify-center">
                  <Upload className="w-4 h-4 sm:w-5 sm:h-5 text-[#B8460E]" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">
                    Submit Creative Assets
                  </h2>
                  <p className="text-gray-400 text-sm sm:text-base">
                    Upload your creative materials for review
                  </p>
                </div>
              </div>

              {error && (
                <div className="bg-red-900/30 border border-red-500/40 rounded-xl p-4 flex items-center space-x-3 backdrop-blur-xl mb-6">
                  <XCircle className="w-6 h-6 text-red-400" />
                  <span className="text-red-300 font-medium">{error}</span>
                </div>
              )}

              {success && (
                <div className="bg-green-900/30 border border-green-500/40 rounded-xl p-4 flex items-center space-x-3 backdrop-blur-xl mb-6">
                  <CheckCircle className="w-6 h-6 text-green-400" />
                  <span className="text-green-300 font-medium">{success}</span>
                </div>
              )}

              <div className="space-y-4">
                <label className="block">
                  <span className="text-white font-semibold mb-2 block">
                    Google Drive Link
                  </span>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="url"
                      value={driveLink}
                      onChange={(e) => setDriveLink(e.target.value)}
                      placeholder="https://drive.google.com/drive/folders/..."
                      className="flex-1 bg-black/40 backdrop-blur-xl border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-400
                               focus:outline-none focus:ring-2 focus:ring-[#B8460E] focus:border-[#B8460E] 
                               hover:bg-black/50 hover:border-white/30 transition-all duration-200
                               disabled:opacity-50 disabled:cursor-not-allowed"
                      required
                      disabled={isSubmitting}
                    />
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !driveLink.trim()}
                      className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-[#B8460E] to-[#8B3A0E] text-white rounded-xl font-semibold text-sm sm:text-base
                               hover:from-[#8B3A0E] hover:to-[#6B2D0B] transition-all duration-200 
                               flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed
                               shadow-lg shadow-[#B8460E]/25"
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                      )}
                      <span>{isSubmitting ? "Submitting..." : "Submit"}</span>
                    </button>
                  </div>
                </label>
                <div className="flex items-start sm:items-center space-x-2 sm:space-x-3 text-gray-400">
                  <Info className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 sm:mt-0" />
                  <p className="text-sm sm:text-base">
                    Ensure your folder permissions are set to "Anyone with the
                    link can view"
                  </p>
                </div>
              </div>
            </Card>

            {/* Submission Deadline */}
            <Card className="bg-gradient-to-r from-red-900/40 to-red-800/40 border border-red-700/50 p-4 sm:p-6">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-700/50 rounded-xl flex items-center justify-center">
                  <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-red-300" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-1 sm:mb-2">
                    Submission Deadline
                  </h3>
                  <p className="text-gray-300 text-sm sm:text-base">
                    All creative materials must be submitted by{" "}
                    <span className="text-white font-bold">10:00 PM EST</span>{" "}
                    the night before launch to allow sufficient review time.
                  </p>
                </div>
              </div>
            </Card>

            {/* How to Organize Your Files - Complete Guide */}
            <Card className="bg-gray-800 text-white p-8">
              <h2 className="text-3xl font-bold mb-8">
                How to Organize Your Files
              </h2>

              {/* Step 1: Create Your Main Folder with Brand */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6">
                  Step 1: Create Your Main Folder with Brand
                </h3>
                <p className="text-gray-300 mb-6 text-lg">
                  Choose which brand you're creating content for, then create
                  your main folder:
                </p>
                <p className="text-gray-400 mb-4">Name format:</p>

                <div className="bg-black rounded-lg p-4 mb-8">
                  <code className="text-white font-mono text-lg">
                    Brand Creatives YourName (e.g., "Hercules Creatives Gray")
                  </code>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div className="bg-white rounded-xl p-6 flex items-center gap-4">
                    <div className="text-4xl">📁</div>
                    <span className="text-black font-semibold text-xl">
                      Hercules Creatives Gray
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-6 flex items-center gap-4">
                    <div className="text-4xl">📁</div>
                    <span className="text-black font-semibold text-xl">
                      Aphrodite Creatives Gray
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center">
                  <p className="text-gray-400">For Hercules brand products</p>
                  <p className="text-gray-400">For Aphrodite brand products</p>
                </div>
              </div>

              {/* Step 2: Create Content Type Folders */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6">
                  Step 2: Create Content Type Folders
                </h3>
                <p className="text-gray-300 mb-8 text-lg">
                  Inside your main folder, create folders based on the content
                  you're making:
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div className="bg-white rounded-xl p-3 sm:p-4 flex items-center gap-3">
                    <div className="text-3xl sm:text-4xl">📁</div>
                    <span className="text-black font-semibold text-base sm:text-xl">
                      Images
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-3 sm:p-4 flex items-center gap-3">
                    <div className="text-3xl sm:text-4xl">📁</div>
                    <span className="text-black font-semibold text-base sm:text-xl">
                      Videos
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-6 mb-6 sm:mb-8 text-center">
                  <p className="text-gray-400 text-sm sm:text-base">
                    Create if you're making image content
                  </p>
                  <p className="text-gray-400 text-sm sm:text-base">
                    Create if you're making video content
                  </p>
                </div>

                <div className="w-full border-l-4 border-blue-500 bg-gray-700 p-3 sm:p-4 rounded-lg">
                  <p className="text-blue-300 text-sm sm:text-lg">
                    <span className="font-semibold">Note:</span> You can create
                    just Images, just Videos, or both folders depending on what
                    type of content you're producing.
                  </p>
                </div>
              </div>

              {/* Step 3: Create Date Subfolders */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6">
                  Step 3: Create Date Subfolders
                </h3>
                <p className="text-gray-300 mb-4 text-lg">
                  Inside your content folders (Images and/or Videos), create
                  date folders:
                </p>
                <p className="text-gray-400 mb-4">Format:</p>

                <div className="bg-black rounded-lg p-3 sm:p-4 mb-6 sm:mb-8">
                  <code className="text-white font-mono text-lg">M/D/YY</code>
                </div>

                <div className="bg-white rounded-xl p-3 sm:p-4 flex items-center gap-3 max-w-md">
                  <div className="text-3xl sm:text-4xl flex-shrink-0">📁</div>
                  <span className="text-black font-semibold text-base sm:text-xl">
                    5/18/25
                  </span>
                </div>
              </div>

              {/* Step 4: Name Your Files */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold mb-6">
                  Step 4: Name Your Files
                </h3>
                <p className="text-gray-300 mb-4 text-lg">
                  Inside each date folder, name your files:
                </p>
                <p className="text-gray-400 mb-4">Format:</p>

                <div className="bg-black rounded-lg p-3 sm:p-4 mb-6 sm:mb-8">
                  <code className="text-white font-mono text-base sm:text-lg">
                    Name.Brand.Product.Number
                  </code>
                </div>

                <div className="bg-white rounded-xl p-3 sm:p-4 flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="text-3xl sm:text-4xl flex-shrink-0">🖼️</div>
                  <div className="flex-1 overflow-x-auto custom-scrollbar-thin">
                    <span className="text-black font-semibold text-sm sm:text-base whitespace-nowrap">
                      Gray.hercules.gummies.59
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
                  <div className="bg-gray-700 rounded-lg p-3 text-left overflow-x-auto custom-scrollbar-thin">
                    <code className="text-gray-300 font-mono whitespace-nowrap text-sm sm:text-base">
                      Gray.hercules.Gummies.1
                    </code>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3 text-left overflow-x-auto custom-scrollbar-thin">
                    <code className="text-gray-300 font-mono whitespace-nowrap text-sm sm:text-base">
                      Gray.aphrodite.Honey.1
                    </code>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3 text-left overflow-x-auto custom-scrollbar-thin">
                    <code className="text-gray-300 font-mono whitespace-nowrap text-sm sm:text-base">
                      Gray.hercules.Mate.1
                    </code>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-3 text-left overflow-x-auto custom-scrollbar-thin">
                    <code className="text-gray-300 font-mono whitespace-nowrap text-sm sm:text-base">
                      Gray.aphrodite.Probiotic.1
                    </code>
                  </div>
                </div>

                {/* IMPORTANT: Never Reset Your Numbers */}
                <div className="border-l-4 border-blue-500 bg-gray-700 p-8 rounded-r-lg">
                  <h4 className="text-xl font-bold text-blue-300 mb-6 flex items-center gap-3">
                    <AlertTriangle className="h-6 w-6" />
                    IMPORTANT: Never Reset Your Numbers!
                  </h4>

                  <p className="text-gray-300 mb-8 text-lg">
                    Each product has its own unique counter that continues
                    forever. Think of it like a lifetime ID number for each
                    creative you make for that product.
                  </p>

                  <div className="bg-gray-800 rounded-lg p-6 mb-8">
                    <h5 className="text-white font-bold mb-6 text-xl">
                      How the numbering works:
                    </h5>
                    <div className="space-y-4 text-gray-300 text-lg">
                      <div className="flex items-start gap-3">
                        <span className="text-blue-400 text-xl">•</span>
                        <span>
                          Every time you create content for{" "}
                          <strong className="text-white">Gummies</strong>, use
                          the next number in sequence (1, 2, 3, 4...)
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-blue-400 text-xl">•</span>
                        <span>
                          Every time you create content for{" "}
                          <strong className="text-white">Honey</strong>, use the
                          next number in sequence (1, 2, 3, 4...)
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-blue-400 text-xl">•</span>
                        <span>
                          Every time you create content for{" "}
                          <strong className="text-white">Mate</strong>, use the
                          next number in sequence (1, 2, 3, 4...)
                        </span>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-blue-400 text-xl">•</span>
                        <span>
                          Every time you create content for{" "}
                          <strong className="text-white">Probiotic</strong>, use
                          the next number in sequence (1, 2, 3, 4...)
                        </span>
                      </div>
                    </div>

                    <div className="mt-8 p-4 bg-gray-700 rounded-lg">
                      <p className="text-blue-400 font-semibold text-lg mb-2">
                        Key point:
                      </p>
                      <p className="text-gray-300 text-lg">
                        Each product keeps its own separate counter that NEVER
                        resets, even across different days, weeks, or months.
                      </p>
                    </div>
                  </div>

                  {/* Day Examples */}
                  <div className="space-y-8">
                    <div className="border-l-4 border-blue-400 pl-6">
                      <h4 className="text-white font-bold mb-4 text-xl">
                        Day 1
                      </h4>
                      <p className="text-gray-400 mb-6 text-lg">
                        You create your first creatives for some products:
                      </p>

                      <div className="space-y-4">
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.hercules.Gummies.1
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (first Gummies creative ever)
                          </span>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.aphrodite.Honey.1
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (first Honey creative ever)
                          </span>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.hercules.Mate.1
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (first Mate creative ever)
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-l-4 border-blue-400 pl-6">
                      <h4 className="text-white font-bold mb-4 text-xl">
                        Day 2
                      </h4>
                      <p className="text-gray-400 mb-6 text-lg">
                        You create more creatives, continuing each product's
                        counter:
                      </p>

                      <div className="space-y-4">
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.aphrodite.Probiotic.1
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (first Probiotic creative ever)
                          </span>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.hercules.Gummies.2
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (second Gummies creative)
                          </span>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.aphrodite.Honey.2
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (second Honey creative)
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="border-l-4 border-blue-400 pl-6">
                      <h4 className="text-white font-bold mb-4 text-xl">
                        Day 3
                      </h4>
                      <p className="text-gray-400 mb-6 text-lg">
                        Counters continue from where they left off:
                      </p>

                      <div className="space-y-4">
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.hercules.Mate.2
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (second Mate creative)
                          </span>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-4">
                          <div className="w-full max-w-full overflow-x-auto custom-scrollbar-thin">
                            <code className="inline-block min-w-max text-green-400 text-sm sm:text-lg font-mono whitespace-nowrap">
                              Gray.aphrodite.Probiotic.2
                            </code>
                          </div>
                          <span className="block text-gray-500 text-xs sm:text-base mt-1">
                            (second Probiotic creative)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 p-6 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border border-yellow-500/30 rounded-lg">
                    <h4 className="text-yellow-300 font-bold mb-3 text-xl">
                      Remember:
                    </h4>
                    <p className="text-yellow-100 text-lg">
                      If you've already created 59 Gummies creatives, your next
                      Gummies creative will be numbered 60, even if it's weeks
                      later. Never go back to 1!
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Technical Requirements */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Video Requirements */}
              <Card className="bg-gray-800 text-white p-8">
                <h3 className="text-2xl font-bold mb-6">Video Requirements</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300 text-lg">Format:</span>
                    <span className="text-white font-semibold text-lg">
                      MP4, MOV
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300 text-lg">Duration:</span>
                    <span className="text-white font-semibold text-lg">
                      15-60 seconds
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300 text-lg">Aspect Ratio:</span>
                    <span className="text-white font-semibold text-lg">
                      9:16 (Stories/Reels), 4:3 (Feed), 1:1 (Square)
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-300 text-lg">File Size:</span>
                    <span className="text-white font-semibold text-lg">
                      No limit
                    </span>
                  </div>
                </div>
              </Card>

              {/* Image Requirements */}
              <Card className="bg-gray-800 text-white p-8">
                <h3 className="text-2xl font-bold mb-6">Image Requirements</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300 text-lg">Format:</span>
                    <span className="text-white font-semibold text-lg">
                      JPG, PNG
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300 text-lg">
                      Min Resolution:
                    </span>
                    <span className="text-white font-semibold text-lg">
                      1080×1080
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-700">
                    <span className="text-gray-300 text-lg">Aspect Ratio:</span>
                    <span className="text-white font-semibold text-lg">
                      9:16 (Stories), 4:3 (Feed), 1:1 (Square)
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-300 text-lg">File Size:</span>
                    <span className="text-white font-semibold text-lg">
                      Under 30MB
                    </span>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sharing Settings */}
            <Card className="bg-gradient-to-r from-purple-600 to-purple-700 p-8">
              <h3 className="text-2xl font-bold text-white mb-6">
                Sharing Settings
              </h3>
              <p className="text-purple-100 mb-8 text-lg">
                Make sure your Google Drive folder is set to:
              </p>
              <div className="text-center">
                <p className="text-white text-2xl font-bold bg-purple-800/50 rounded-xl px-8 py-6 inline-block">
                  "Anyone with the link can view"
                </p>
              </div>
            </Card>

            {/* Important Notice */}
            <Card className="bg-gray-700 p-8">
              <h3 className="text-2xl font-bold text-white mb-4">
                Important Notice
              </h3>
              <p className="text-gray-300 text-lg">
                Late submissions will not be accepted. The media buyer needs
                time to review and prepare campaigns for the next day.
              </p>
            </Card>

            {isSubmitted && (
              <Card className="bg-green-600/20 border border-green-500/30 p-8 text-center">
                <CheckCircle2 className="h-16 w-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">
                  Submission Complete!
                </h3>
                <p className="text-gray-300">
                  Your creative assets have been submitted successfully. The
                  media buyer will review them shortly.
                </p>
              </Card>
            )}
          </div>
        ) : (
          /* My Submissions Tab */
          <div className="space-y-6">
            <Card className="p-8">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-[#B8460E]/20 to-[#8B3A0E]/20 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-[#B8460E]" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-white">
                    Your Submissions
                  </h2>
                  <p className="text-gray-400 text-lg">
                    Track your creative submission history
                  </p>
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-12 h-12 animate-spin text-[#B8460E]" />
                </div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-16">
                  <FolderOpen className="w-20 h-20 text-gray-400 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-gray-400 mb-4">
                    No submissions yet
                  </h3>
                  <p className="text-gray-500 text-lg">
                    Submit your first creative to get started
                  </p>
                </div>
              ) : (
                <>
                  {/* Mobile list */}
                  <div className="md:hidden space-y-3">
                    {submissions.map((submission) => (
                      <div
                        key={submission.id}
                        className="bg-black/30 backdrop-blur-2xl border border-white/10 rounded-xl p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={`px-2 py-1 rounded-full text-[11px] font-semibold capitalize border ${getStatusColor(
                              submission.status
                            )}`}
                          >
                            {submission.status}
                          </span>
                          <div className="text-[11px] text-gray-400">
                            {new Date(
                              submission.submitted_at
                            ).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                            ,{" "}
                            {new Date(
                              submission.submitted_at
                            ).toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>

                        <a
                          href={submission.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-blue-400 hover:text-blue-300 transition-colors overflow-x-auto custom-scrollbar-thin whitespace-nowrap"
                        >
                          <span className="inline-flex items-center gap-2">
                            <LinkIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm">
                              {submission.link_url}
                            </span>
                          </span>
                        </a>

                        {submission.reviewer_notes && (
                          <div className="mt-2 p-3 bg-black/40 rounded-lg border border-white/10">
                            <p className="text-xs text-gray-300">
                              <span className="font-semibold text-white">
                                Review Notes:
                              </span>{" "}
                              {submission.reviewer_notes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Desktop table */}
                  <div className="hidden md:block bg-black/30 backdrop-blur-2xl border border-white/10 rounded-xl overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-black/50 border-b border-white/10 px-6 py-4">
                      <div className="grid grid-cols-9 gap-4 items-center">
                        <div className="col-span-2">
                          <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                            Status
                          </h3>
                        </div>
                        <div className="col-span-5">
                          <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                            Link
                          </h3>
                        </div>
                        <div className="col-span-2">
                          <h3 className="text-sm font-semibold text-white uppercase tracking-wide">
                            Date Submitted
                          </h3>
                        </div>
                      </div>
                    </div>

                    {/* Table Body */}
                    <div className="divide-y divide-white/10">
                      {submissions.map((submission) => (
                        <div
                          key={submission.id}
                          className="px-6 py-4 hover:bg-black/40 transition-all duration-200"
                        >
                          <div className="grid grid-cols-9 gap-4 items-center">
                            {/* Status Column */}
                            <div className="col-span-2">
                              <div className="flex items-center space-x-2">
                                {getStatusIcon(submission.status)}
                                <span
                                  className={`px-3 py-1 rounded-full text-xs font-semibold capitalize border ${getStatusColor(
                                    submission.status
                                  )}`}
                                >
                                  {submission.status}
                                </span>
                              </div>
                            </div>

                            {/* Link Column */}
                            <div className="col-span-5">
                              <a
                                href={submission.link_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors group"
                              >
                                <LinkIcon className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm font-medium truncate group-hover:underline">
                                  {submission.link_url.length > 50
                                    ? `${submission.link_url.substring(0, 50)}...`
                                    : submission.link_url}
                                </span>
                              </a>
                              {submission.reviewer_notes && (
                                <div className="mt-2 p-3 bg-black/50 rounded-lg border border-white/10">
                                  <p className="text-xs text-gray-300">
                                    <span className="font-semibold text-white">
                                      Review Notes:
                                    </span>{" "}
                                    {submission.reviewer_notes}
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Date Column */}
                            <div className="col-span-2">
                              <div className="text-sm text-gray-300">
                                <div className="font-medium">
                                  {new Date(
                                    submission.submitted_at
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  })}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {new Date(
                                    submission.submitted_at
                                  ).toLocaleTimeString("en-US", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreativeSubmissionPage;
